import PropTypes from "prop-types";
import * as React from "react";
import {
  OTHER_PRODUCT_TYPE,
  composerReducer,
  createComposer,
  presetFor,
  toBatchRequest,
} from "../../../domain/productComposer/composer";
import {
  composerProblems,
  problemsOnPage,
} from "../../../domain/productComposer/problems";
import SignType from "../../../domain/signType";
import { createProducts } from "../../../services/productComposer";
import { useNotification } from "../../../services/Notifications";
import { Button } from "../../../themeV2";
import ComposerShell from "../ComposerShell";
import ConfirmDialog from "../ConfirmDialog";
import DiscardDialog from "../DiscardDialog";
import ImageEditor from "../images/ImageEditor";
import UploadStatus from "../images/UploadStatus";
import useBatchUploader from "../images/useBatchUploader";
import useComposerClose from "../useComposerClose";
import PresetPage from "./PresetPage";
import PricingPage from "./PricingPage";
import ProductsPage from "./ProductsPage";
import ReviewPage from "./ReviewPage";

const STEPS = [
  { key: "preset", label: "Product type" },
  { key: "product", label: "Products" },
  { key: "pricing", label: "Pricing" },
  { key: "review", label: "Review" },
];

const stepIndexOf = (key) => STEPS.findIndex((s) => s.key === key);

const hasWork = (state) =>
  state.cards.some((card) => card.name || card.images.length > 0);

const zeroSplitsOf = (state) =>
  state.signedTypes.flatMap((signedType) =>
    state.signers
      .filter((signer) => (signer.splits[signedType] ?? 0) === 0)
      .map((signer) => `${signer.name} on ${SignType.toLabel(signedType)}`),
  );

export default function CreateComposer({
  shop,
  productTypes,
  otherBumpPercentage,
  otherImageRequirements,
  existingProducts,
  isAdmin,
  user,
  appearInstantly,
  onClose,
}) {
  const [state, dispatch] = React.useReducer(
    composerReducer,
    { shop },
    createComposer,
  );
  const [stepIndex, setStepIndex] = React.useState(0);
  const [attempted, setAttempted] = React.useState(() => new Set());
  const [revealToken, setRevealToken] = React.useState(0);
  const [confirming, setConfirming] = React.useState(null);
  const [isSaving, setSaving] = React.useState(false);
  const [server, setServer] = React.useState({ problems: [], message: null });
  const notify = useNotification();
  const closing = useComposerClose({ isDirty: hasWork(state), onClose });

  const step = STEPS[stepIndex].key;
  const productType = productTypes.find(
    (t) => t.productTypeId === state.productTypeId,
  );
  const problems = composerProblems({
    state,
    productTypes,
    existingProducts,
    user,
  });
  const pageProblems = problemsOnPage(problems, step);
  const bumpFacts = productType
    ? {
        bumpPercentage: productType.bumpPercentage,
        additionalBumpAmount: productType.additionalBumpAmount ?? 0,
      }
    : { bumpPercentage: otherBumpPercentage ?? 0, additionalBumpAmount: 0 };
  const requirements = productType ?? otherImageRequirements;
  const aspectRatio = productType?.aspectRatio ?? 1;
  const productTypeName = (productType ?? OTHER_PRODUCT_TYPE).name;
  const uploader = useBatchUploader({
    dispatch,
    productTypeId: state.productTypeId,
    aspectRatio,
    requirements,
  });

  const applyPreset = (preset) =>
    dispatch({
      type: "choosePreset",
      preset: presetFor({ preset, productTypes, shop }),
    });

  const choosePreset = (preset) =>
    hasWork(state)
      ? setConfirming({ kind: "reset", run: () => applyPreset(preset) })
      : applyPreset(preset);

  const chooseType = (productTypeId) =>
    hasWork(state)
      ? setConfirming({
          kind: "reset",
          run: () => {
            applyPreset(state.preset);
            dispatch({ type: "setProductType", productTypeId });
          },
        })
      : dispatch({ type: "setProductType", productTypeId });

  const advance = () => setStepIndex((index) => index + 1);

  const refuse = (page) => {
    setAttempted((seen) => new Set([...seen, page]));
    setRevealToken((token) => token + 1);
  };

  const next = () => {
    const isPresetReady =
      step !== "preset" ||
      (Boolean(state.preset) && state.productTypeId !== undefined);
    const blocked = pageProblems.length > 0 || !isPresetReady;
    const zeroSplits = step === "pricing" ? zeroSplitsOf(state) : [];

    return blocked
      ? refuse(step)
      : zeroSplits.length > 0
      ? setConfirming({ kind: "zeroSplit", detail: zeroSplits, run: advance })
      : advance();
  };

  const submit = async () => {
    setSaving(true);
    const result = await createProducts(toBatchRequest(state));
    setSaving(false);

    if (result.ok) {
      const count = result.data.products.length;
      notify({
        message: `${count} product${count === 1 ? "" : "s"} created`,
        type: "success",
      });
      closing.close({ shouldRefreshInfluencer: true });
    } else {
      setServer({ problems: result.problems, message: result.message });
      setRevealToken((token) => token + 1);
    }
  };

  const isLast = stepIndex === STEPS.length - 1;
  const primaryAction = isLast ? (
    <Button
      variant="filled"
      loading={isSaving}
      onClick={() => (problems.length > 0 ? refuse("review") : submit())}
    >
      Create
    </Button>
  ) : (
    <Button variant="filled" onClick={next}>
      Next
    </Button>
  );

  return (
    <>
      <ComposerShell
        open={closing.open}
        onRequestClose={closing.requestClose}
        onClosed={closing.onClosed}
        title="Add products"
        steps={{ items: STEPS, current: stepIndex }}
        revealToken={revealToken}
        appearInstantly={appearInstantly}
        footer={
          <>
            {stepIndex > 0 && (
              <Button variant="text" onClick={() => setStepIndex((i) => i - 1)}>
                Back
              </Button>
            )}
            {primaryAction}
          </>
        }
      >
        {step === "preset" && (
          <PresetPage
            state={state}
            productTypes={productTypes}
            shop={shop}
            isAdmin={isAdmin}
            onChoosePreset={choosePreset}
            onChooseType={chooseType}
            showErrors={attempted.has("preset")}
          />
        )}
        {step === "product" && (
          <ProductsPage
            state={state}
            dispatch={dispatch}
            uploader={uploader}
            problems={problems}
            showErrors={attempted.has("product")}
          />
        )}
        {step === "pricing" && (
          <PricingPage
            state={state}
            dispatch={dispatch}
            productType={productType}
            bumpFacts={bumpFacts}
            isAdmin={isAdmin}
            user={user}
            problems={pageProblems}
            showErrors={attempted.has("pricing")}
          />
        )}
        {step === "review" && (
          <ReviewPage
            state={state}
            bumpFacts={bumpFacts}
            aspectRatio={aspectRatio}
            productTypeName={productTypeName}
            isAdmin={isAdmin}
            problems={problems}
            serverProblems={server.problems}
            serverMessage={server.message}
            onEditStep={(key) => setStepIndex(stepIndexOf(key))}
          />
        )}
      </ComposerShell>

      <ConfirmDialog
        open={confirming?.kind === "reset"}
        title="Start over?"
        description="Changing this resets the product, its images and its pricing."
        confirmLabel="Start over"
        destructive
        onConfirm={() => {
          confirming.run();
          setConfirming(null);
        }}
        onCancel={() => setConfirming(null)}
      />

      <ConfirmDialog
        open={confirming?.kind === "zeroSplit"}
        title="Continue with a $0 split?"
        description={`These signers would earn nothing: ${(
          confirming?.detail ?? []
        ).join(
          ", ",
        )}. This is usually only right when an agent takes the whole payout.`}
        confirmLabel="Continue"
        onConfirm={() => {
          confirming.run();
          setConfirming(null);
        }}
        onCancel={() => setConfirming(null)}
      />

      {uploader.editingSource && (
        <ImageEditor
          source={uploader.editingSource}
          aspectRatio={aspectRatio}
          requirements={requirements}
          onCancel={uploader.cancelEdit}
          onSave={uploader.saveEdit}
        />
      )}

      <UploadStatus cards={state.cards} />

      <DiscardDialog {...closing.discard} />
    </>
  );
}

CreateComposer.propTypes = {
  existingProducts: PropTypes.arrayOf(PropTypes.object).isRequired,
  appearInstantly: PropTypes.bool,
  isAdmin: PropTypes.bool,
  onClose: PropTypes.func,
  otherBumpPercentage: PropTypes.number,
  otherImageRequirements: PropTypes.object,
  productTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  shop: PropTypes.object.isRequired,
  user: PropTypes.shape({
    isAdmin: PropTypes.bool,
    level: PropTypes.string,
    permissions: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};
